import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Building2, Star, MapPin, Phone, ChevronLeft, X, Loader2, Users, GitBranch, Send, Save } from "lucide-react";

/**
 * Facilities — Doctor's practice locations. First-class multi-clinic setup.
 * A doctor can have any number of facilities (hospitals + own clinic + telemedicine).
 * Each availability profile + each appointment is tied to one facility.
 */
const TYPE_LABEL = { clinic: "Clinic", hospital: "Hospital", telemedicine: "Telemedicine", home_visit: "Home visit" };

export default function Facilities() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [managing, setManaging] = useState(null);   // facility whose staff/workflow we're managing

  const load = async () => {
    try {
      const { data } = await api.get("/facilities");
      setItems(data.items || []);
    } catch { toast.error("Failed to load facilities"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({
    name: "", type: "clinic", address: "", city: "", pincode: "", phone: "",
    active: true, is_primary: items.length === 0, sort_order: (items.length + 1) * 10,
  });

  const save = async (payload) => {
    try {
      if (payload.id) await api.patch(`/facilities/${payload.id}`, payload);
      else            await api.post("/facilities", payload);
      toast.success("Facility saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (f) => {
    if (!window.confirm(`Delete "${f.name}"? Historical appointments will keep the link but the facility will be hidden from booking flows.`)) return;
    try {
      const { data } = await api.delete(`/facilities/${f.id}`);
      toast.success(data.deactivated ? "Facility deactivated (has appointments)" : "Facility deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <Layout>
      <div className="card-soft" data-testid="facilities-page">
        <div className="flex items-center justify-between p-6 border-b border-[#EFECE5]">
          <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-[#47554D] hover:text-[#0A4C36]" data-testid="facilities-back"><ChevronLeft size={16} /> Back</button>
          <h1 className="text-xl font-semibold tracking-tight text-[#0A4C36]" style={{ fontFamily: "Instrument Sans" }}>My facilities</h1>
          <button onClick={startNew} className="btn-emerald" data-testid="facility-new-btn"><Plus size={14} /> Add facility</button>
        </div>

        <div className="p-6 md:p-8">
          <p className="text-sm text-[#5C7568] mb-6">
            Practice locations where you see patients &mdash; hospitals you&rsquo;re associated with, your own clinic, telemedicine, home-visit slots.
            Availability and appointments are tracked <b>per facility</b>. The primary facility is where new bookings default when a facility isn&rsquo;t specified.
          </p>
          {loading ? <Loading /> : (
            <>
              {items.length === 0 && (
                <div className="py-16 text-center">
                  <div className="text-lg font-semibold text-[#1F2A24]" style={{ fontFamily: "Instrument Sans" }}>No facilities yet</div>
                  <div className="text-sm text-[#8A9992] mt-2 mb-6">Add your first practice location to unlock availability & appointment tracking per site.</div>
                  <button onClick={startNew} className="btn-emerald" data-testid="facility-empty-add"><Plus size={14} /> Add facility</button>
                </div>
              )}
              <div className="space-y-3">
                {items.map((f) => (
                  <div key={f.id} className={`rounded-2xl border p-4 md:p-5 ${f.active ? "border-[#EFECE5] bg-white" : "border-[#EFECE5] bg-[#F7F4EC]/60 opacity-70"}`} data-testid={`facility-row-${f.id}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <div className="w-10 h-10 rounded-2xl bg-[#0A4C36]/8 text-[#0A4C36] flex items-center justify-center flex-none"><Building2 size={16} /></div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-semibold text-[#1F2A24] text-base" style={{ fontFamily: "Instrument Sans" }}>{f.name}</div>
                            {f.is_primary && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#8A5A34] bg-[#FCE7D9] px-2 py-0.5 rounded-full uppercase tracking-widest"><Star size={9} /> Primary</span>
                            )}
                            <span className="text-[10px] font-bold text-[#0A4C36] bg-[#E6F0EC] px-2 py-0.5 rounded-full uppercase tracking-widest">{TYPE_LABEL[f.type] || f.type}</span>
                            {!f.active && <span className="text-[10px] font-bold text-[#8A9992] bg-[#F3F1EC] px-2 py-0.5 rounded-full uppercase tracking-widest">Inactive</span>}
                          </div>
                          {(f.address || f.city || f.pincode) && (
                            <div className="text-xs text-[#5C7568] mt-1 flex items-center gap-1 flex-wrap"><MapPin size={11} /> {[f.address, f.city, f.pincode].filter(Boolean).join(", ")}</div>
                          )}
                          {f.phone && <div className="text-xs text-[#5C7568] mt-0.5 flex items-center gap-1"><Phone size={11} /> {f.phone}</div>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => setManaging(f)} className="btn-outline-emerald !py-1.5 !px-3 text-xs" data-testid={`facility-manage-${f.id}`}><Users size={12} /> Staff & workflow</button>
                        <button onClick={() => setEditing(f)} className="btn-outline-emerald !py-1.5 !px-3 text-xs" data-testid={`facility-edit-${f.id}`}><Pencil size={12} /> Edit</button>
                        <button onClick={() => del(f)} className="text-[#B14A2A] hover:bg-[#FCE7D9] rounded-lg p-2" data-testid={`facility-delete-${f.id}`}><Trash2 size={14} /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {editing && <FacilityDrawer initial={editing} onClose={() => setEditing(null)} onSave={save} />}
      {managing && <ManageDrawer facility={managing} onClose={() => setManaging(null)} />}
    </Layout>
  );
}


// --- Manage staff + workflow --------------------------------------------
const ROLE_META = [
  { key: "receptionist", label: "Receptionist", desc: "Checks patients in, verifies appointments" },
  { key: "nurse",        label: "Nurse",         desc: "Captures vitals, prepares for consultation" },
  { key: "pharmacist",   label: "Pharmacist",    desc: "Dispenses medicines, generates bill" },
  { key: "lab_tech",     label: "Lab technician", desc: "Processes lab orders, marks referrals" },
];

function ManageDrawer({ facility, onClose }) {
  const [tab, setTab] = useState("staff");
  return (
    <div className="fixed inset-0 bg-black/40 flex items-end md:items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-t-3xl md:rounded-3xl w-full md:max-w-2xl max-h-[92vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="manage-drawer">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#5C7568]">Manage</div>
            <div className="text-lg font-semibold text-[#1F2A24]" style={{ fontFamily: "Instrument Sans" }}>{facility.name}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F3F1EC] flex items-center justify-center"><X size={16} /></button>
        </div>
        <div className="flex gap-2 mb-4">
          <button onClick={() => setTab("staff")} className={`pill-tab ${tab === "staff" ? "pill-tab-active" : "pill-tab-inactive"}`} data-testid="mng-tab-staff"><Users size={12} /> Staff</button>
          <button onClick={() => setTab("workflow")} className={`pill-tab ${tab === "workflow" ? "pill-tab-active" : "pill-tab-inactive"}`} data-testid="mng-tab-workflow"><GitBranch size={12} /> Workflow</button>
        </div>
        {tab === "staff" ? <StaffPanel facility={facility} /> : <WorkflowPanel facility={facility} />}
      </div>
    </div>
  );
}

function StaffPanel({ facility }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ name: "", email: "", roles: [] });

  const load = async () => {
    try { const { data } = await api.get(`/facilities/${facility.id}/staff`); setItems(data.items || []); }
    catch { toast.error("Failed to load staff"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [facility.id]);

  const toggleRole = (key) => setF({ ...f, roles: f.roles.includes(key) ? f.roles.filter((r) => r !== key) : [...f.roles, key] });

  const invite = async () => {
    if (!f.name.trim() || !f.email.trim() || f.roles.length === 0) return toast.error("Name, email and at least one role are required");
    try {
      const { data } = await api.post(`/facilities/${facility.id}/staff`, f);
      toast.success(data.new_user ? "Invite email sent" : "Existing user added");
      setF({ name: "", email: "", roles: [] }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invite failed"); }
  };

  const updateStaff = async (s, patch) => {
    try { await api.patch(`/facilities/${facility.id}/staff/${s.id}`, { roles: patch.roles ?? s.roles, active: patch.active ?? s.active }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const removeStaff = async (s) => {
    if (!window.confirm(`Remove ${s.name} from ${facility.name}?`)) return;
    try { await api.delete(`/facilities/${facility.id}/staff/${s.id}`); toast.success("Removed"); load(); }
    catch { toast.error("Remove failed"); }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-[#EFECE5] p-4 bg-[#FDFBF7]">
        <div className="text-xs font-semibold text-[#5C7568] uppercase tracking-widest mb-3">Invite a team member</div>
        <div className="grid grid-cols-2 gap-2">
          <input placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="input-clean" data-testid="staff-name" />
          <input placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="input-clean" data-testid="staff-email" />
        </div>
        <div className="grid grid-cols-2 gap-2 mt-3">
          {ROLE_META.map((r) => (
            <label key={r.key} className={`rounded-xl px-3 py-2 cursor-pointer border ${f.roles.includes(r.key) ? "border-[#0A4C36] bg-[#E6F0EC]" : "border-[#EFECE5] bg-white"}`}>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={f.roles.includes(r.key)} onChange={() => toggleRole(r.key)} className="accent-[#0A4C36]" data-testid={`role-${r.key}`} />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#1F2A24]">{r.label}</div>
                  <div className="text-[11px] text-[#8A9992] leading-tight">{r.desc}</div>
                </div>
              </div>
            </label>
          ))}
        </div>
        <div className="flex justify-end mt-3">
          <button onClick={invite} className="btn-emerald text-sm" data-testid="staff-invite"><Send size={13} /> Send invite</button>
        </div>
      </div>

      <div>
        <div className="text-xs font-semibold text-[#5C7568] uppercase tracking-widest mb-3">Team members</div>
        {loading ? <div className="py-6 flex justify-center text-[#8A9992]"><Loader2 size={14} className="animate-spin" /></div> :
          items.length === 0 ? <div className="text-[#8A9992] text-sm py-4">No staff yet. If it&rsquo;s just you (the doctor), you don&rsquo;t need to add anyone.</div> :
          <div className="space-y-2">
            {items.map((s) => (
              <div key={s.id} className="rounded-xl border border-[#EFECE5] p-3 flex items-center gap-3" data-testid={`staff-row-${s.id}`}>
                <div className="w-9 h-9 rounded-full bg-[#0A4C36]/8 text-[#0A4C36] flex items-center justify-center font-semibold text-xs">
                  {(s.name || s.email).slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#1F2A24] truncate">{s.name}</div>
                  <div className="text-[11px] text-[#8A9992] truncate">{s.email}</div>
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {(s.roles || []).map((r) => <span key={r} className="text-[10px] font-bold text-[#0A4C36] bg-[#E6F0EC] px-2 py-0.5 rounded-full uppercase tracking-widest">{r.replace("_", " ")}</span>)}
                    {!s.active && <span className="text-[10px] font-bold text-[#8A9992] bg-[#F3F1EC] px-2 py-0.5 rounded-full uppercase tracking-widest">Inactive</span>}
                  </div>
                </div>
                <button onClick={() => updateStaff(s, { active: !s.active })} className="text-xs text-[#0A4C36] underline" data-testid={`staff-toggle-${s.id}`}>{s.active ? "Deactivate" : "Activate"}</button>
                <button onClick={() => removeStaff(s)} className="text-[#B14A2A] hover:bg-[#FCE7D9] rounded-lg p-2" data-testid={`staff-remove-${s.id}`}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>}
      </div>
    </div>
  );
}

function WorkflowPanel({ facility }) {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const { data } = await api.get(`/facilities/${facility.id}/workflow`); setSteps(data.steps); }
    catch { toast.error("Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [facility.id]);
  const toggle = (key) => setSteps(steps.map((s) => s.key === key ? { ...s, enabled: !s.enabled } : s));
  const save = async () => {
    setBusy(true);
    try { await api.put(`/facilities/${facility.id}/workflow`, { steps }); toast.success("Workflow saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="py-6 flex justify-center text-[#8A9992]"><Loader2 size={14} className="animate-spin" /></div>;

  return (
    <div>
      <p className="text-sm text-[#5C7568] mb-4">
        Turn on the stages your team handles at <b>{facility.name}</b>. If a stage is off, <b>you (the doctor) will do it yourself</b> inline during consultation. <b>Consultation</b> is always on.
      </p>
      <div className="space-y-2">
        {steps.map((s, i) => (
          <div key={s.key} className={`rounded-2xl border p-3 flex items-center gap-3 ${s.enabled ? "border-[#0A4C36]/25 bg-[#E6F0EC]" : "border-[#EFECE5] bg-[#F7F4EC]"}`} data-testid={`wf-step-${s.key}`}>
            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-xs font-bold text-[#0A4C36]">{i + 1}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[#1F2A24]">{s.label}</div>
              <div className="text-[11px] text-[#8A9992]">Role: {s.role.replace("_", " ")}</div>
            </div>
            {s.key === "consultation" ? (
              <span className="text-[10px] font-bold text-[#0A4C36] uppercase tracking-widest">Always on</span>
            ) : (
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" checked={s.enabled} onChange={() => toggle(s.key)} data-testid={`wf-toggle-${s.key}`} />
                <div className="w-10 h-6 bg-[#EFECE5] rounded-full peer peer-checked:bg-[#0A4C36] transition-colors" />
                <div className="absolute left-0.5 top-0.5 bg-white w-5 h-5 rounded-full transition-transform peer-checked:translate-x-4" />
              </label>
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-end mt-4">
        <button onClick={save} disabled={busy} className="btn-emerald text-sm" data-testid="wf-save"><Save size={13} /> {busy ? "Saving…" : "Save workflow"}</button>
      </div>
    </div>
  );
}


// --- existing FacilityDrawer (unchanged) ---------------------------------

function FacilityDrawer({ initial, onClose, onSave }) {
  const [f, setF] = useState(initial);
  const setV = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <div className="fixed inset-0 bg-black/40 flex items-end md:items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-t-3xl md:rounded-3xl w-full md:max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="facility-drawer">
        <div className="flex items-center justify-between mb-4">
          <div className="text-lg font-semibold text-[#1F2A24]" style={{ fontFamily: "Instrument Sans" }}>{f.id ? "Edit facility" : "New facility"}</div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F3F1EC] flex items-center justify-center"><X size={16} /></button>
        </div>
        <div className="space-y-3">
          <Field label="Name" required>
            <input value={f.name} onChange={(e) => setV("name", e.target.value)} className="input-clean" placeholder="e.g. Apollo Hospital Whitefield" data-testid="facility-name" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <select value={f.type} onChange={(e) => setV("type", e.target.value)} className="input-clean" data-testid="facility-type">
                <option value="clinic">Clinic</option>
                <option value="hospital">Hospital</option>
                <option value="telemedicine">Telemedicine</option>
                <option value="home_visit">Home visit</option>
              </select>
            </Field>
            <Field label="Phone">
              <input value={f.phone} onChange={(e) => setV("phone", e.target.value)} className="input-clean" data-testid="facility-phone" />
            </Field>
          </div>
          <Field label="Address">
            <input value={f.address} onChange={(e) => setV("address", e.target.value)} className="input-clean" data-testid="facility-address" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="City"><input value={f.city} onChange={(e) => setV("city", e.target.value)} className="input-clean" data-testid="facility-city" /></Field>
            <Field label="Pincode"><input value={f.pincode} onChange={(e) => setV("pincode", e.target.value)} className="input-clean" data-testid="facility-pincode" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Sort order"><input type="number" value={f.sort_order} onChange={(e) => setV("sort_order", Number(e.target.value))} className="input-clean" /></Field>
            <Field label="Status">
              <select value={f.active ? "1" : "0"} onChange={(e) => setV("active", e.target.value === "1")} className="input-clean" data-testid="facility-active">
                <option value="1">Active</option><option value="0">Inactive</option>
              </select>
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm mt-1">
            <input type="checkbox" checked={f.is_primary} onChange={(e) => setV("is_primary", e.target.checked)} className="w-4 h-4 accent-[#0A4C36]" data-testid="facility-primary" />
            <span>Set as primary facility <span className="text-[#8A9992]">(default for new bookings)</span></span>
          </label>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="btn-outline-emerald text-sm">Cancel</button>
          <button onClick={() => onSave(f)} className="btn-emerald text-sm" data-testid="facility-save">Save</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="field-label mb-1.5">{label}{required && <span className="text-[#B14A2A] ml-0.5">*</span>}</div>
      {children}
    </label>
  );
}

function Loading() { return <div className="py-16 flex items-center justify-center text-[#8A9992]"><Loader2 className="animate-spin mr-2" size={16} /> Loading…</div>; }
